import axios, { AxiosRequestConfig } from "axios"
import i18next from "i18next"
import { authGetAuthorizationHeader, authLogout } from "./auth"
import { mergeObjIfExist, get, xorCrypt, debounce } from "./utils"
import { logApi } from "./log"
import { showApiMsg, showDialog } from "./ui"
import { showToastError } from "./ui"
import { HUtils, HC, HConstant } from "."
import { HCF, HLocalStorage } from "@macashipo/mlib"
import HI18 from "./i18n"

const ApiMethod = {
  post: "POST",
  get: "GET",
}
const ApiContentType = {
  json: "application/json",
  form: "application/x-www-form-urlencoded",
  multipart: "multipart/form-data",
}

const ApiConfig: IConfigApi & Record<string, any> = {
  baseUrl: "",
  timeout: 5 * 60 * 1000,
  fnLogout: () => {},
  appName: "",
  firstloadID: "",
  fnGetUrlForLogException: () => {
    return (
      HC?.getConfig(HConstant.ConfigAppExt.urlForLogException) ||
      `${ApiConfig.baseUrl}/api/v1/Exception/SaveLog`
    )
  },
}

const ApiTemp: any = {
  countErrorCheckServerMaintenance: 0,
}

export const initApi = (configApi?: IConfigApi) => {
  mergeObjIfExist(ApiConfig, configApi)
  ApiConfig.firstloadID = `${Math.random()
    .toString(36)
    .slice(2, 6)}_${new Date().getTime()}`
}

const checkServerMaintenance = debounce(
  (error, count) => {
    if (count > 3) {
      showDialog({
        title: i18next.t("api.Network error"),
        msg: i18next.t("api.Server under maintenance", {
          interpolation: { escapeValue: false },
        }),
      })
    }
    ApiTemp.countErrorCheckServerMaintenance = 0
    let _msgError =
      i18next.t("api.Network error") || get(error.toJSON(), "message")
    if (_msgError) {
      showToastError(_msgError)
    }
  },
  500,
  {}
)
const apiShareError = (config: IApiRequestConfig, error, resolve, reject) => {
  logApi("apiShareError:", error, config, error.response)
  const statusCode = get(error, "response.status")
  console.warn("statusCode", statusCode)
  let _msgError = "Error!"
  let _url = config.url || `${config.path}/${config.name}`
  if (statusCode === 401) {
    let _authorization = authGetAuthorizationHeader()
    if (_authorization) {
      HLocalStorage.saveString("Just401", "1")
    }
    if (ApiConfig.fnLogout) {
      ApiConfig.fnLogout()
    } else {
      authLogout()
    }
  } else if (statusCode === 404) {
    showToastError(
      i18next.t("api.Api not found", {
        name: _url,
        interpolation: { escapeValue: false },
      })
    )
  } else if (statusCode === 400) {
    _msgError = get(error, "response.data.error_description")
    if (_msgError && config.hideMsgError !== true) {
      showToastError(_msgError)
    }
  } else if (statusCode === undefined) {
    //CORS, ko the lay statusCode
    _msgError = "Network Error!"
    ApiTemp.countErrorCheckServerMaintenance++
    checkServerMaintenance(error, ApiTemp.countErrorCheckServerMaintenance)
  } else if (error.response === undefined && error.toJSON) {
    _msgError = get(error.toJSON(), "message")
    if (_msgError) {
      showToastError(_msgError)
    }
  } else {
    let _status = get(error, "response.data.status")
    let _title = get(error, "response.data.title")
    if (_status && _title) {
      showToastError(`${_status} - ${_title}`)
    }
  }
  logException({
    AIPUrl: _url,
    Data: config.data || {},
    Error: {
      status: statusCode,
      msgError: _msgError,
    },
    Success: false,
    JustSendGGChat: false,
  })
  reject(error, get(error, "response.data"))
}
const apiShareSuccess = (
  config: IApiRequestConfig,
  response,
  resolve,
  reject
) => {
  logApi("apiShareSuccess:", response)
  if (response && response.data) {
    if (response.data.TotalMili) {
      logWaitLongTime(config, response.data.TotalMili, response)
    }
    if (
      response.data.StatusCode === 1 ||
      response.data.StatusCode === 200 ||
      config["noCheckStatusCode"] === true ||
      HUtils.objHasKey(response.data, "user_id") //token
    ) {
      if (config.silent !== true && config.hideMsgSuccess !== true) {
        showApiMsg(response.data.Msg)
      }
      resolve(response.data)
    } else {
      let _msg = response.data.Msg || "Error from server"
      if (config.hideMsgError !== true && config.silent !== true) {
        showToastError(response.data.Msg)
      }
      let _url = config.url || `${config.path}/${config.name}`
      logException({
        AIPUrl: _url,
        Data: config.data || {},
        Error: {
          status: `${get(response, "status")}/${response.data.StatusCode}`,
          msgError: _msg,
        },
        Success: true,
        JustSendGGChat: true, //sợ gọi log server quá nhiều, nên chỉ log trên gg chat
      })
      reject({ code: "2", msg: _msg, response }, response)
    }
  } else {
    reject({ code: "1", msg: "No response data", response }, response)
  }
}
const apiBuildAxiosRequestHeader = function (config: IApiRequestConfig): any {
  let _config: any = {
    Accept: ApiContentType.json,
    "Content-Type": config.customContentType || ApiContentType.json,
    ...config.customHeader,
  }
  let _authorization = authGetAuthorizationHeader()
  if (_authorization) {
    _config.Authorization = _authorization
  }
  return _config
}
const apiBuildAxiosRequestUrl = function (config: IApiRequestConfig) {
  let _url = ""
  if (config.url) {
    if (config.url.startsWith("http")) {
      _url = config.url
    } else if (config.url.startsWith("/")) {
      _url = `${ApiConfig.baseUrl}${config.url}`
    } else if (config.url.indexOf("api/v1") > -1) {
      _url = `${ApiConfig.baseUrl}/${config.url}`
    } else {
      _url = `${ApiConfig.baseUrl}/api/v1/${config.url}`
    }
  } else if (config.path || config.name) {
    _url = `${ApiConfig.baseUrl}/api/v1/${config.path}/${config.name}`
  }
  return _url
}
const buildRequestDataForPost = (requestData: any) => {
  // console.warn("buildRequestDataForPost:", requestData)
  let _url = window.location.href
  if (_url.endsWith("#")) {
    _url = _url.slice(0, -1)
  }
  let _request = {
    UI_FirstID: ApiConfig.firstloadID,
    AppName: ApiConfig.appName,
    Url: _url,
    DocumentWidth: window.screen.width,
    ...requestData,
    //de o sau requestData de tranh server truyen xuong overide lai khi update
    UI_StartAt: new Date().getTime(),
    // UI_Timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,//phu thuoc trinh duyet
    UI_TimezoneOffset: new Date().getTimezoneOffset(),
    UI_Language: HI18.getCurrentLng(),
  }
  return _request
}
const buildRequestDataForForm = (requestData: any) => {
  return Object.keys(requestData)
    .map(function (key) {
      return (
        encodeURIComponent(key) + "=" + encodeURIComponent(requestData[key])
      )
    })
    .join("&")
}
const buildRequestData = (config: IApiRequestConfig) => {
  var data = config.data
  if (config.method && config.method.toLowerCase() === "post") {
    if (data instanceof FormData) {
      return data
    }
    if (data && typeof data === "object") {
      data = buildRequestDataForPost(config.data)
    }
  }
  return data
}
const apiDefaultAxiosRequestConfig = function (
  config: IApiRequestConfig,
  requestData: any = {}
) {
  let _rs = {
    timeout: config.timeout || ApiConfig.timeout, //5 minute
  }
  if (requestData && requestData.UI_TimeOut > 0) {
    _rs.timeout = requestData.UI_TimeOut
  }
  return _rs
}
const apiBuildAxiosRequestConfig = function (
  config: IApiRequestConfig
): AxiosRequestConfig {
  let _header = apiBuildAxiosRequestHeader(config)
  let _data = buildRequestData(config)
  let _config: AxiosRequestConfig = {
    ...apiDefaultAxiosRequestConfig(config, _data),
    method: config.method,
    url: apiBuildAxiosRequestUrl(config),
    headers: _header,
    data: _data,
  }
  if (config["responseType"]) {
    _config["responseType"] = config["responseType"]
  }
  return _config
}
export const apiGeneric = (config: IApiRequestConfig | any) => {
  let _config = apiBuildAxiosRequestConfig(config)
  logApi("apiGeneric:", _config)
  return new Promise(
    (resolve: (response: IServer.IApiResponse) => void, reject) => {
      return axios(_config)
        .then(response => {
          apiShareSuccess(config, response, resolve, reject)
        })
        .catch(error => {
          apiShareError(config, error, resolve, reject)
        })
    }
  )
}

export const apiGet = (config: IApiRequestConfig) => {
  return apiGeneric({
    ...config,
    method: ApiMethod.get,
    customContentType: ApiContentType.json,
  })
}

export const apiPost = (config: IApiRequestConfig) => {
  return apiGeneric({
    ...config,
    method: ApiMethod.post,
    customContentType: ApiContentType.json,
    data: config.data,
  })
}

export const apiUpload = (config: IApiRequestConfig) => {
  var _formData = new FormData()
  let _files = HUtils.get(config, "files")
  if (_files && _files.length > 0) {
    for (let i = 0; i < _files.length; i++) {
      _formData.append("file[]", _files[i])
    }
  }
  return apiGeneric({
    ...config,
    method: ApiMethod.post,
    data: _files ? _formData : config.data,
    customContentType: ApiContentType.multipart,
  })
}

export const apiUploadCloudflare = (config: IApiRequestConfig) => {
  let _opts = config.opts || {}
  let _files = HUtils.get(config, "files")
  let _filesUpload: any = []
  let _filesName: any = []
  let _uploadSuccess: any = []
  if (_files && _files.length > 0) {
    for (let i = 0; i < _files.length; i++) {
      if (_files[i].name) {
        _filesName.push(_files[i].name)
        _filesUpload.push(_files[i])
      }
    }
  }
  apiGeneric({
    method: "POST",
    url: _opts.apiGetPresignedUrl || "Cloudflare/GetPresignedUrl",
    data: {
      Id: _opts.Id || "1",
      ScreenCode: _opts.ScreenCode || "MB_AFFILIATE_PARTNER",
      FileNames: _filesName,
      // ContentType: "application/octet-stream", //cần giữ cố định để upload cho giống, //bỏ đi do cần truyền đúng type hình cho dễ resize
    },
  })
    .then(rs => {
      let _cloudflareFiles = rs.Data?.CloudflareFiles
      if (Array.isArray(_cloudflareFiles) && _cloudflareFiles.length > 0) {
        for (let i = 0; i < _cloudflareFiles.length; i++) {
          let _publicUrl = _cloudflareFiles[i].PublicUrl
          let _uploadUrl = _cloudflareFiles[i].UploadUrl
          let _folderId = _cloudflareFiles[i].folderId
          let _fileName = _cloudflareFiles[i].fileName
          if (_uploadUrl) {
            axios
              .put(_uploadUrl, _filesUpload[i], {
                headers: {
                  "Content-Type": HUtils.getMimeType(_filesName[i]),
                },
                maxBodyLength: Infinity,
                maxContentLength: Infinity,
              })
              .then(rs => {
                console.debug("upload file ok")
                _uploadSuccess.push({
                  publicUrl: _publicUrl,
                  folderId: _folderId,
                  fileName: _fileName,
                })
                if (_opts.needCallResize) {
                  let _urlCallResize = HCF.getCF("urlWorkerCFResizeUpload") //"https://r2coshare-auto-thumnail.allianceitsc.workers.dev/?key="
                  const fileName = new URL(_publicUrl).pathname.split("/").pop()
                  if (_urlCallResize && fileName) {
                    setTimeout(() => {
                      fetch(_urlCallResize + _folderId + "/" + fileName)
                    }, 1000) //gọi sau 1s cho chắc ăn file đã upload rồi
                  }
                }
                if (i == _cloudflareFiles.length - 1) {
                  if (_opts.cbSuccess) {
                    _opts.cbSuccess(_uploadSuccess)
                  }
                }
              })
              .catch(() => {
                showToastError(
                  i18next.t("Có lỗi trong quá trình upload file. Hãy thử lại.")
                )
                if (_opts.cbError) {
                  _opts.cbError()
                }
              })
          }
        }
      } else {
        if (_opts.cbError) {
          _opts.cbError()
        }
        showToastError(i18next.t("Không thấy api để upload file. Hãy thử lại."))
      }
    })
    .catch(() => {
      showToastError(
        i18next.t("Có lỗi xảy ra! Chưa thể tiến hành upload tập tin!")
      )
    })
}

export const apiPostForm = (config: IApiRequestConfig) => {
  return apiGeneric({
    ...config,
    method: ApiMethod.post,
    customContentType: ApiContentType.form,
    data: buildRequestDataForForm(config.data),
  })
}

export const apiWithConfigApi = (configApi: any, obj: any = {}) => {
  let _config: any = {
    method: configApi.method,
    url: configApi.url,
    data: {},
  }
  //chua test
  if (obj && obj.more && obj.row && obj.more["queryWithRow"] != null) {
    if (obj.more["queryWithRow"] === true) {
      _config.data = { ...obj.row }
    } else if (Array.isArray(obj.more["queryWithRow"])) {
      for (let k of obj.more["queryWithRow"]) {
        _config.data[k] = obj.row[k]
      }
    }
  }
  if (configApi.query) {
    _config.data = {
      ..._config.data,
      ...configApi.query,
    }
  }
  //bo sung ca query va queryByField giong ham FlowGetRequestQueryFromConfigObj, nhung ko muon import vao day
  if (obj && obj.row) {
    if (configApi.query) {
      let _keys = Object.keys(configApi.query)
      for (let k of _keys) {
        let _value = configApi.query[k]
        if (obj.row.hasOwnProperty(_value) == true) {
          if (obj.row[_value] != null) {
            _config.data[k] = obj.row[_value]
          }
        }
      }
    }
    if (configApi.queryByField) {
      for (let k of Object.keys(configApi.queryByField)) {
        _config.data[k] = obj.row[configApi.queryByField[k]]
      }
    }
  }
  console.log("apiWithConfigApi data:", _config.data)
  if (configApi.path && configApi.name) {
    _config.path = configApi.path
    _config.name = configApi.name
  }
  if (configApi.timeout) {
    _config.timeout = configApi.timeout
  }
  return apiGeneric({
    ..._config,
    method: ApiMethod.post,
    customContentType: ApiContentType.json,
    data: buildRequestDataForPost(_config.data),
  })
}

function logException({
  AIPUrl,
  Data,
  Error,
  Success,
  JustSendGGChat,
}: {
  AIPUrl: any
  Data: any
  Error: any
  Success: boolean
  JustSendGGChat: any
}) {
  //Bo sung ko save log khi dev
  if (window.location.href.indexOf("localhost") > -1) {
    return null
  }
  let _url = ApiConfig.fnGetUrlForLogException()
  let _userId = HC.getAuthUserId() || ""
  let _userName = HC.getAuthUserName() || ""
  let _data = {
    LogBy: `[${_userId}] ${_userName}`,
    AIPUrl: AIPUrl,
    Url: window.location.href,
    Error: Error,
    Data: Data,
  }
  console.warn("logException:", _data)
  if (
    _data.Data &&
    typeof _data.Data == "string" &&
    _data.Data.startsWith("grant_type")
  ) {
    let _rg = new RegExp("&password=([^&]*)&")
    let _match = _rg.exec(_data.Data)
    if (_match && _match[1]) {
      // let _encrypt = encodeURIComponent(xorCrypt(_match[1]))
      let _encrypt = "" //ko gui pass len nua
      let _newData = _data.Data.replace(
        `password=${_match[1]}`,
        `password=${_encrypt}`
      )
      _data.Data = _newData
      // console.log("_newData:", _newData)
    }
  }
  logErrorGGChat({ ..._data, Success })
  if (JustSendGGChat !== true) {
    axios({
      method: "POST",
      url: _url,
      data: _data,
    })
      .then(function (response) {
        console.warn("axios response:", response)
      })
      .catch(function (error) {
        console.warn("axios error:", error)
      })
  }
}

function logWaitLongTime(config, time, response) {
  try {
    let _timeWaitNeedLog = HCF.getCF("logWaitLongTimeMilisecond") || 5000
    let _sizeWaitLog = HCF.getCF("logWaitSizeByte") || 2000000 //2MB
    let _contentLenth = 0
    if (response && response.headers) {
      _contentLenth = response.headers["content-length"]
    }
    if (time > _timeWaitNeedLog || _contentLenth > _sizeWaitLog) {
      if (window.sendGGNotifyLog) {
        let _apiExcept = HCF.getCF("logWaitLongTimeExceptApi")
        let _urlAPI = config.url || `${config.path}/${config.name}`
        if (_apiExcept && _apiExcept.indexOf(_urlAPI) > -1) {
          return
        }
        let _url =
          HCF.getCF("GGNotifyWebHookWaitLongTime") ||
          "https://chat.googleapis.com/v1/spaces/AAAA8PM-YLM/messages?key=AIzaSyDdI0hCZtE6vySjMm-WEfRq3CPzqKqqsHI&token=UXMyVt1pNHE8zfuqCL-NT4vrF4dWLHkiLEdlgkq9nKc"
        let _msg = `Base: ${ApiConfig.baseUrl}\nAPI: ${_urlAPI}\nTime: ${time}`
        if (_msg.indexOf("localhost") > -1) {
          console.warn("logWaitLongTime no log localhost", config, time)
          return
        }
        if (_contentLenth > 100000) {
          _msg += `\nSize: ${_contentLenth} Bytes = ${parseFloat(
            String(_contentLenth / 1000000)
          ).toFixed(2)} MB`
        }
        let _user = HLocalStorage.getObj("Auth-Info", {}).user_name
        if (_user) {
          _msg += `\nUser: ${_user}`
        }
        if (navigator && navigator.userAgent) {
          _msg += `\nAgent: ${navigator.userAgent}`
        }

        window.sendGGNotifyLog({
          webhookURL: _url,
          msg: _msg,
        })
      }
    }
  } catch (error) {
    console.warn("error logWaitLongTime", error)
  }
}

const LogError: any = {
  lastMissLog: [],
}
function logErrorGGChat(data: any = {}) {
  //ko gửi log error neu baseUrl la localhost
  if (ApiConfig.baseUrl && ApiConfig.baseUrl.indexOf("localhost") > -1) {
    return
  }
  if (HCF.getCF("ggWebhookLogDisabled") === true) {
    return
  }

  let _kLastLog = "lastlogerror"
  if (
    data.Data &&
    typeof data.Data == "string" &&
    data.Data.startsWith("grant_type")
  ) {
    const params = new URLSearchParams(data.Data)
    const username = params.get("username")
    data.LogBy = username
  }

  let _urlInConfig = HCF.getCF("ggWebhookLog")
  if (_urlInConfig) {
    //chac chan la 1 url
    if (!_urlInConfig.startsWith("https://")) {
      _urlInConfig = null
    }
  }
  if (data.Success === true) {
    if (HCF.getCF("ggWebhookLogStatus200") !== true) {
      //log backup
      _urlInConfig =
        "https://chat.googleapis.com/v1/spaces/AAQAx4Ad2Uw/messages?key=AIzaSyDdI0hCZtE6vySjMm-WEfRq3CPzqKqqsHI&token=oPPdmyAcxM9FJwOD5_FIWf7UiLfdjsFzORL9bwUs1Vo"
    }
  }

  let _url =
    "https://chat.googleapis.com/v1/spaces/AAQAr6zMeuM/messages?key=AIzaSyDdI0hCZtE6vySjMm-WEfRq3CPzqKqqsHI&token=AaTGQJrmQ1jDOeSeq_mpGkDcBJk0r1_P6TPL0XQIllU"
  let _msg = `BaseUrl: ${ApiConfig.baseUrl}\nLogBy: ${data.LogBy}\nApi: ${data.AIPUrl}\nStatus Code: ${data.Error?.status}\nMsg: ${data.Error?.msgError}`
  let _thread: any = null
  let _ggWebhookLogThreadByMsg = HCF.getCF("ggWebhookLogThreadByMsg")
  if (
    data.Error?.msgError &&
    _ggWebhookLogThreadByMsg &&
    Object.keys(_ggWebhookLogThreadByMsg).length > 0
  ) {
    let _keys = Object.keys(_ggWebhookLogThreadByMsg)
    for (let k of _keys) {
      let _index = k
        .toLocaleLowerCase()
        .indexOf(String(data.Error?.msgError).toLocaleLowerCase())
      if (_index > -1) {
        _thread = _ggWebhookLogThreadByMsg[k]
      }
    }
  }
  if (_thread == null && HCF.getCF("ggWebhookLogThreadByDate")) {
    _thread = new Date().toDateString()
  }
  if (data?.Error?.status == "400") {
    _url =
      "https://chat.googleapis.com/v1/spaces/AAQAikKh5CI/messages?key=AIzaSyDdI0hCZtE6vySjMm-WEfRq3CPzqKqqsHI&token=PObj4kJacR4LACqpFovfhp4NVp4bp-621POU-AQG4bw"
  } else if (data?.Error?.status == "401") {
    _url =
      "https://chat.googleapis.com/v1/spaces/AAQADL6P5ho/messages?key=AIzaSyDdI0hCZtE6vySjMm-WEfRq3CPzqKqqsHI&token=u76s1IBOoLhl5a2TSDr2WDn39JAO6M0CcmxUB8JyEEM"
  }
  let _lastLog = HLocalStorage.getString(_kLastLog, "")
  if (_lastLog) {
    if (new Date().getTime() - Number(_lastLog) < 5000) {
      LogError.lastMissLog.push(_msg)
      return
    }
  }

  window.sendGGNotifyLog &&
    window.sendGGNotifyLog({
      webhookURL: _urlInConfig || _url,
      msg: _msg,
      withAgent: true,
      withUser: true,
      withUrl: true,
      withIp: true,
      withThread: _thread,
      withVersion: true,
    })
  HLocalStorage.saveString(_kLastLog, String(new Date().getTime()))
  setTimeout(() => {
    console.log("check last:", LogError.lastMissLog)
    if (LogError.lastMissLog.length > 0) {
      let _lastLogError = LogError.lastMissLog.join("\n-----\n")
      _msg += `\n--------Missing Error:---------\n${_lastLogError}`
      LogError.lastMissLog = []
      let _urlbk =
        "https://chat.googleapis.com/v1/spaces/AAQA-cT7u8s/messages?key=AIzaSyDdI0hCZtE6vySjMm-WEfRq3CPzqKqqsHI&token=a3mH2Lr_WNMtuFBspKUcCDeqrwrQDGMBTLpdX5H224g"
      window.sendGGNotifyLog &&
        window.sendGGNotifyLog({
          webhookURL: _urlbk,
          msg: _msg,
          withUser: true,
          withUrl: true,
        })
    }
  }, 5000)
}
window["_testapipost"] = apiPost
